import axios from 'axios'

// Resolution order:
//   1. VITE_BFF_BASE_URL — absolute URL baked at build time (prod / GH Pages).
//   2. VITE_BFF_PORT     — dev override; defaults to 8080 against localhost.
function getBffBaseUrl(): string {
  const baked = import.meta.env.VITE_BFF_BASE_URL?.trim()
  if (baked) return baked.replace(/\/$/, '')
  const port = import.meta.env.VITE_BFF_PORT ?? 8080
  return `http://localhost:${port}`
}

export { getBffBaseUrl }

export const apiClient = axios.create({
  baseURL: getBffBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
  // Send the BFF session cookie cross-origin so /api/me can identify the user
  // and /api/logout can clear the session. Requires CORS allowCredentials=true
  // on the server (handled by WebConfig when allowed-origins is not literal "*").
  withCredentials: true,
})

// CSRF: read the XSRF-TOKEN cookie (set by Spring's CookieCsrfTokenRepository
// with httpOnly=false, domain=.mpm.technology) and echo as X-XSRF-TOKEN on
// every mutating request. Axios's built-in xsrfCookieName only fires on
// same-origin requests, so we do it manually for the cross-subdomain case.
const MUTATING_METHODS = new Set(['post', 'put', 'delete', 'patch'])
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}
apiClient.interceptors.request.use(config => {
  const method = config.method?.toLowerCase()
  if (method && MUTATING_METHODS.has(method)) {
    const token = readCookie('XSRF-TOKEN')
    if (token) config.headers.set('X-XSRF-TOKEN', token)
  }
  return config
})

// On 401: kick the user to /login. The hash-router redirect happens via a soft
// event the app listens for — keeps this module router-agnostic and avoids a
// circular dep with React Router. /api/me itself is whitelisted because it
// returns 204 for anonymous (not 401) — see WhoAmIController.
const AUTH_REDIRECT_EVENT = 'lens:auth-required'
apiClient.interceptors.response.use(
  r => r,
  error => {
    if (error?.response?.status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_REDIRECT_EVENT))
    }
    return Promise.reject(error)
  },
)
export { AUTH_REDIRECT_EVENT }

// ── Types ──────────────────────────────────────────────────────────────────────

export type DeviceType = 'MOBILE' | 'DESKTOP'

export interface AuditRequest {
  url: string
  device: DeviceType
  /** Phase 6: optional source-code attachment. */
  source?: SourceSpec
}

export interface AuditStartedResponse {
  auditId: string
  wsSubscribePath: string
}

export interface ReportSummary {
  id: string
  url: string
  device: DeviceType
  performanceScore: number
  accessibilityScore: number
  bestPracticesScore: number
  seoScore: number
  createdAt: string
  runLocation: 'HOSTED' | 'LOCAL'
}

export interface ReportDetail extends ReportSummary {
  completedAt: string | null
  rawReport: Record<string, unknown> | null
  /** Phase 6: source attached to the audit, if any. PAT (when git) is never returned. */
  sourceKind: 'LOCAL' | 'GIT' | null
  sourceIdentifier: string | null
}

export type AuditPhase = 'loading' | 'auditing' | 'scoring' | 'complete' | 'error'

export interface AuditProgressMessage {
  auditId: string
  phase: AuditPhase
  progress: number
  message: string
  result?: ReportDetail
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const auditsApi = {
  start: (req: AuditRequest) =>
    apiClient.post<AuditStartedResponse>('/api/audit', req).then(r => r.data),

  listReports: (page = 0, size = 20) =>
    apiClient.get<{ content: ReportSummary[]; totalElements: number }>('/api/reports', {
      params: { page, size },
    }).then(r => r.data),

  getReport: (id: string) =>
    apiClient.get<ReportDetail>(`/api/reports/${id}`).then(r => r.data),

  deleteReport: (id: string) =>
    apiClient.delete(`/api/reports/${id}`).then(r => r.data),

  deleteAllReports: () =>
    apiClient.delete<{ deleted: number }>('/api/reports').then(r => r.data),

  /** Absolute URL for the HTML export — used as an <a href="…" download> target. */
  exportHtmlUrl: (id: string) =>
    `${getBffBaseUrl()}/api/reports/${id}/export/html`,

  /** Phase 6: persisted AI suggestions for a completed audit. */
  getSuggestions: (id: string) =>
    apiClient.get<AiSuggestionMessage[]>(`/api/reports/${id}/suggestions`).then(r => r.data),

  /**
   * Client-gathered excerpt flow (REFACTOR_FILE_ACCESS.md): which checks want
   * suggestions and the globs/caps to gather local excerpts with.
   */
  getSuggestionPlan: (id: string) =>
    apiClient.get<SuggestionPlan>(`/api/reports/${id}/suggestion-plan`).then(r => r.data),

  /**
   * Submits gathered excerpts and kicks off async generation (202). Results
   * stream on /topic/audit/{id}/ai — same protocol as audit-time attachment.
   */
  generateSuggestions: (id: string, req: GenerateSuggestionsRequest) =>
    apiClient.post(`/api/reports/${id}/suggestions`, req).then(() => undefined),

  /** Stores a Lighthouse result the desktop app ran on this machine. */
  importLocal: (req: { url: string; device: DeviceType; lhr: unknown; reportHtml: string | null }) =>
    apiClient.post<{ id: string }>('/api/audit/import', req).then(r => r.data),
}

// ── Client-gathered source excerpts (REFACTOR_FILE_ACCESS.md) ─────────────────

export interface SuggestionPlanCheck {
  id: string
  title: string
  globs: string[]
}

export interface SuggestionPlan {
  auditId: string
  checks: SuggestionPlanCheck[]
  skipDirs: string[]
  allowedExtensions: string[]
  maxFilesPerCheck: number
  maxBytesPerCheck: number
}

export interface GenerateSuggestionsRequest {
  checks: {
    auditCheckId: string
    files: { path: string; content: string; truncated: boolean }[]
  }[]
}

// ── Anonymous "try-once" demo ─────────────────────────────────────────────────

export interface AnonymousAuditResult {
  id: string
  performanceScore: number
  accessibilityScore: number
  bestPracticesScore: number
  seoScore: number
  rawReport: Record<string, unknown>
}

export const anonymousAuditsApi = {
  /** Anonymous audits accept URL + device only — no source attachment. */
  start: (req: { url: string; device: DeviceType }) =>
    apiClient.post<AuditStartedResponse>('/api/audit/anonymous', req).then(r => r.data),

  /** Returns the cached LHR or null if the audit isn't done / has expired. */
  getResult: (id: string) =>
    apiClient.get<AnonymousAuditResult>(`/api/audit/anonymous/${id}`)
      .then(r => r.data)
      .catch(err => {
        if (err?.response?.status === 404) return null
        throw err
      }),
}

// ── Auth (OAuth via Spring Security; session cookie carries the identity) ────

export interface CurrentUser {
  email: string
  name: string
  provider: 'google' | 'github' | 'email'
}

export const authApi = {
  /** 200 with the user when signed in; 204 (empty body) when anonymous. */
  me: () =>
    apiClient.get<CurrentUser | ''>('/api/me').then(r =>
      // axios resolves 204 with empty body as '' — coerce that to null so callers
      // can treat "signed out" as a single sentinel.
      r.status === 204 || r.data === '' ? null : (r.data as CurrentUser),
    ),

  /** Clears the BFF session. The FE should then reload or navigate. */
  logout: () => apiClient.post('/api/logout').then(() => undefined),

  /**
   * Asks the BFF to email a 6-digit sign-in code. The BFF answers 204 whatever
   * happened (unknown address, rate limit, success), so this resolves the same
   * way in every case except a malformed address (400).
   */
  requestCode: (email: string) =>
    apiClient.post('/api/auth/request-code', { email }).then(() => undefined),

  /** Exchanges a code for a session cookie. Rejects with a 400 for a wrong or expired code. */
  verifyCode: (email: string, code: string) =>
    apiClient.post<CurrentUser>('/api/auth/verify', { email, code }).then(r => r.data),

  /**
   * Returns the absolute URL the browser must NAVIGATE to (not fetch) to start
   * the OAuth flow. Must be a real top-level navigation so Spring can set the
   * session cookie that carries the OAuth state across the Google round-trip.
   */
  googleSignInUrl: () => `${getBffBaseUrl()}/oauth2/authorization/google`,
}

// ── Phase 6: Source-code intake ───────────────────────────────────────────────

export type SourceSpec =
  | { kind: 'local'; path: string }
  | { kind: 'git'; url: string; ref?: string; token?: string }

// ── Phase 6: AI / Ollama types ────────────────────────────────────────────────

export interface OllamaHealth {
  available: boolean
  latencyMs: number
  configuredModel: string
  modelLoaded: boolean
  error: string | null
  /** Echo of the configured Ollama endpoint — surfaced in the privacy banner. */
  baseUrl: string
}

export interface OllamaModel {
  name: string
  model: string
  size: number
  digest: string
  modifiedAt: string
}

export type SuggestionConfidence = 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN'

export interface SuggestionEdit {
  file: string
  explanation: string
  patch: string
}

export interface AiSuggestionMessage {
  id: string | null
  auditId: string
  auditCheckId: string | null
  auditTitle: string | null
  summary: string | null
  confidence: SuggestionConfidence | null
  edits: SuggestionEdit[]
  createdAt: string
  /**
   * - `started` — generation began for this audit; UI shows a placeholder card
   * - `delta`   — token chunk; UI appends to the live preview (in `chunk`)
   * - `ready`   — structured suggestion ready; UI replaces preview with card
   * - `error`   — generation failed for one audit, or a pipeline-level error
   * - `done`    — entire stream finished
   */
  type: 'started' | 'delta' | 'ready' | 'error' | 'done'
  error: string | null
  /** Populated only on `delta` frames. */
  chunk: string | null
}

/** In-flight token preview for a single audit's suggestion. */
export interface SuggestionDraft {
  auditCheckId: string
  auditTitle: string | null
  text: string
}

export interface OllamaPullEvent {
  status: string | null
  digest: string | null
  total: number | null
  completed: number | null
  error: string | null
}

export const aiApi = {
  health: () => apiClient.get<OllamaHealth>('/api/ai/health').then(r => r.data),

  listModels: () => apiClient.get<OllamaModel[]>('/api/ai/models').then(r => r.data),

  /**
   * Streams Ollama's pull progress. Returns an abort function the caller can
   * invoke to cancel mid-flight. Each progress line becomes an `onEvent` call;
   * stream completion fires `onDone`; transport / 5xx errors fire `onError`.
   */
  pullModel(
    model: string,
    callbacks: {
      onEvent: (event: OllamaPullEvent) => void
      onDone: () => void
      onError: (message: string) => void
    },
  ): () => void {
    const controller = new AbortController()
    const url = `${getBffBaseUrl()}/api/ai/pull`

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ model }),
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok || !response.body) {
          callbacks.onError(`Pull failed: HTTP ${response.status}`)
          return
        }
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          // SSE frames are separated by blank lines; each frame can carry
          // event:<name>\ndata:<json>
          const frames = buffer.split('\n\n')
          buffer = frames.pop() ?? ''
          for (const frame of frames) {
            const eventLine = frame.split('\n').find(l => l.startsWith('event:'))
            const dataLine = frame.split('\n').find(l => l.startsWith('data:'))
            const eventName = eventLine ? eventLine.slice(6).trim() : 'progress'
            const dataRaw = dataLine ? dataLine.slice(5).trim() : ''
            if (eventName === 'done') {
              callbacks.onDone()
              return
            }
            if (!dataRaw) continue
            try {
              const payload = JSON.parse(dataRaw) as OllamaPullEvent
              if (eventName === 'error' || payload.error) {
                callbacks.onError(payload.error ?? 'Pull failed')
                return
              }
              callbacks.onEvent(payload)
            } catch {
              // ignore malformed line
            }
          }
        }
        callbacks.onDone()
      })
      .catch(err => {
        if (controller.signal.aborted) return
        callbacks.onError(err instanceof Error ? err.message : String(err))
      })

    return () => controller.abort()
  },
}
